import { createFileRoute, Link } from "@tanstack/react-router";

import { cities } from "@/lib/site";
import { IMAGES } from "@/lib/images";
import { JsonLd } from "@/components/site/JsonLd";
import { localBusinessSchema, breadcrumbSchema } from "@/lib/schema";
import { FadeUp } from "@/components/site/FadeUp";
import { SectionHeading } from "@/components/site/SectionHeading";
import { CityCard, CtaBand } from "@/components/site/CTA";
import { Breadcrumbs } from "@/components/site/Breadcrumbs";
import { InnerHero } from "@/components/site/PageBits";
import { useGoToQuote } from "@/lib/quoteNav";

export const Route = createFileRoute("/service-areas/")({
  head: () => ({
    meta: [
      { title: "Service Areas | Pretty in Pink Cleaning Service" },
      {
        name: "description",
        content:
          "Pretty in Pink Cleaning Service serves Wetumpka, Millbrook, Prattville, Montgomery, Tallassee, and Eclectic, AL. See if we clean near you.",
      },
      {
        property: "og:title",
        content: "Service Areas | Pretty in Pink Cleaning Service",
      },
      {
        property: "og:description",
        content:
          "Serving Wetumpka, Millbrook, Prattville, Montgomery, Tallassee, and Eclectic, AL.",
      },
      { property: "og:type", content: "website" },
      { property: "og:image", content: IMAGES.coverBanner },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:image", content: IMAGES.coverBanner },
    ],
  }),
  component: ServiceAreasIndex,
});

function ServiceAreasIndex() {
  const goQuote = useGoToQuote();
  return (
    <>
      <JsonLd data={localBusinessSchema()} />
      <JsonLd
        data={breadcrumbSchema([
          { name: "Home", path: "/" },
          { name: "Service Areas", path: "/service-areas" },
        ])}
      />
      <InnerHero
        eyebrow="Service Areas"
        title="Cleaning across the River Region"
        subtitle="We're based in Wetumpka, Alabama and proudly serve the surrounding communities. Find your city below — or just reach out and we'll confirm we can help."
      >
        <div className="mb-4">
          <Breadcrumbs
            items={[
              { name: "Home", path: "/" },
              { name: "Service Areas", path: "/service-areas" },
            ]}
          />
        </div>
      </InnerHero>

      <section className="px-4 py-12 md:px-6 md:py-16">
        <FadeUp>
          <SectionHeading
            title="Cities we serve"
            intro="Pick your city to see the services we offer there and a little about the area."
          />
        </FadeUp>
        <div className="mx-auto mt-10 grid max-w-6xl gap-5 sm:grid-cols-2 lg:grid-cols-3">
          {cities.map((c, i) => (
            <FadeUp key={c.slug} delay={i * 60}>
              <CityCard slug={c.slug} name={c.name} state={c.state} blurb={c.blurb} />
            </FadeUp>
          ))}
        </div>

        <FadeUp className="mx-auto mt-12 max-w-3xl rounded-2xl border border-border bg-secondary/40 p-6 text-center md:p-8">
          <p className="text-lg text-ink text-pretty">Don't see your town?</p>
          <p className="mt-2 text-muted-foreground text-pretty">
            We serve Wetumpka and the surrounding areas. If you're nearby, just ask — we'll let you
            know if we can help.
          </p>
          <div className="mt-5 flex flex-wrap justify-center gap-3">
            <button
              type="button"
              onClick={goQuote}
              className="btn-sparkle inline-flex items-center justify-center bg-primary px-6 py-3 text-sm font-semibold text-primary-foreground"
            >
              <span>Get a Quote</span>
            </button>
            <Link
              to="/contact"
              className="font-semibold text-primary underline-offset-4 hover:underline"
            >
              Contact us →
            </Link>
          </div>
        </FadeUp>
      </section>

      <CtaBand />
    </>
  );
}
